/**
 * Proof of delivery, and load-in reconciliation.
 *
 * POD is three-part by design:
 *
 *   1. PHOTO — mandatory. The screen cannot be submitted without one, because
 *      the server refuses a POD without a real photo.
 *   2. QUANTITY — what the driver says he handed over, per line.
 *   3. CUSTOMER CONFIRMATION — happens later, in the customer's own app.
 *
 * A photo alone proves goods existed somewhere; it does not prove how many were
 * received. Quantity entry is what makes a later "I only got 8 of 10" argument
 * resolvable instead of a stand-off.
 *
 * POD is also the event that consumes van stock, so it works offline and
 * queues — a driver in a basement godown must still be able to close a stop.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, Empty, Money, PhotoTile, Select, Sheet,
  Skeleton, Stepper, TextArea, TextInput, useToast,
} from '../../components/ui';
import { PaymentCard, derivePayment, type PaymentState } from '../../components/PaymentCard';
import { useQuery, useOnline } from '../../lib/useApi';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { api, ApiError, uploadMedia, postFile } from '../../lib/api';
import * as outbox from '../../lib/outbox';
import { getCoords, previewUrl, revokePreview, type CompressedImage } from '../../lib/camera';
import { CameraSheet } from '../../components/Camera';
import { ImageCaptureSheet } from '../../components/ImageCaptureSheet';
import { ReminderSheet } from '../../components/ReminderSheet';
import { ClassificationBadge, classificationNameStyle } from '../../components/ClassificationBadge';
import { piecesAndPacks, dateShort, daysBetween } from '../../lib/format';

interface InvoiceDetail {
  invoice: {
    id: string; invoice_number: string; net_amount_paise: number;
    balance_amount_paise: number | null; customer_id: string; shop_name: string;
    customer_name?: string; customer_code?: string; customer_classification?: string | null;
  };
  items: {
    line_no: number; sku_code: string; product_name: string; uom: string;
    quantity: number; free_quantity: number;
    // Unit snapshot (0030): quantity is in sold_uom (PCS/BOX); base_quantity is
    // ALWAYS pieces, so the screen can show pets + loose pieces and reconcile
    // with the van stock (which is in pieces).
    sold_uom: string; base_quantity: number | null; pack_qty: number | null;
    bc_pack_uom: string | null; bc_base_uom: string | null;
  }[];
  // The customer's total outstanding across all their open invoices.
  customerOutstandingPaise?: number;
}

/** The customer's whole ledger — every open invoice, for the collect flow. */
interface Ledger {
  customer: { name: string; code: string };
  outstandingPaise: number;
  invoices: {
    id: string; invoice_number: string; invoice_date: string; due_date: string | null;
    balance_amount_paise: number; payment_status: string; status: string;
  }[];
}

const FAILURE_REASONS = [
  'shop_closed', 'customer_refused', 'payment_not_ready',
  'address_not_found', 'vehicle_breakdown', 'other',
] as const;

export function Pod() {
  const t = useT();
  /**
   * ONE SCREEN, TWO WAYS IN.
   *
   * `/pod/:dispatchId`        — a planned stop on a route.
   * `/pod/direct/:invoiceId`  — an on-call delivery with no route at all.
   *
   * The same screen deliberately, because the act is the same: photograph the
   * goods, state the quantities, explain any shortage, record the money. A
   * second screen would be two places for the shortage rules to drift apart,
   * and the rules are the part that settles disputes.
   */
  const { dispatchId, invoiceId: directInvoiceId } = useParams<{
    dispatchId?: string; invoiceId?: string;
  }>();
  const isDirect = !!directInvoiceId;

  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();
  // A driver delivers off their own van — the one their open day was started
  // on. They don't pick a van; the server fills it in. So the "Delivered from"
  // picker is for a salesperson delivering off someone else's van, not a driver.
  const { user } = useAuth();
  // field_agent delivers too (driver + salesperson in one), so they take the
  // driver path on POD.
  const isDriver = user?.role === 'driver' || user?.role === 'field_agent';

  const state = (window.history.state?.usr ?? {}) as {
    shopName?: string; invoiceNumber?: string; loadSheetId?: string; customerName?: string;
  };

  /**
   * Where the POD is sent.
   *
   * Computed once rather than at each call site. The online post and the
   * offline outbox must agree: a queued delivery that replays against the wrong
   * endpoint would fail silently hours later, on a phone nobody is looking at.
   */
  const podPath = directInvoiceId
    ? `/api/deliveries/direct/${directInvoiceId}/pod`
    : `/api/deliveries/${dispatchId}/pod`;

  /**
   * Where to go once it is saved.
   *
   * A route delivery returns to its round. A direct one has no round — sending
   * the person to `/routes/` would land a salesperson on a driver screen
   * showing nothing, which reads as the save having failed.
   *
   * Declared after `state`, which it reads. It was above it, and TypeScript
   * caught the use-before-declaration; worth noting because the value would
   * have been `undefined` at runtime rather than throwing.
   */
  const afterPodPath = directInvoiceId
    ? '/orders'
    : `/routes/${state.loadSheetId ?? ''}`;

  // The dispatch endpoint does not expose invoice item ids directly, so the
  // invoice is fetched to get them — POD lines reference invoice_items.
  const [invoiceId, setInvoiceId] = useState<string | null>(null);

  /**
   * MONEY, ASKED ABOUT AT THE DOOR.
   *
   * Until now this screen recorded goods and said nothing about payment: after
   * the photo the driver went back to his route list, with no prompt and no
   * link to Collect. A driver holding the shop's cash had nothing in front of
   * him asking about it.
   *
   * `null` means the question has not been answered yet, and the submit button
   * refuses until it is — because an unanswered question and a shop that paid
   * nothing look identical afterwards, and only one of them is a fact.
   */
  /**
   * MONEY AT THE DOOR — the approved payment card.
   *
   * Replaces the old full/part/none + split controls with the single card used
   * everywhere money is taken (components/PaymentCard). The driver enters cash
   * and/or UPI against the balance; the card computes what is still owed and,
   * when more than the bill is handed over, the advance. The outcome
   * (full / part / none) is DERIVED from these amounts rather than chosen, so it
   * can never disagree with the money.
   *
   * No discount row at the door: a write-off here would change the invoice
   * balance, which this screen has never done — that is a separate change.
   */
  const [payment, setPayment] = useState<PaymentState>({
    discountPaise: 0, discountReason: '', cashPaise: 0, upiPaise: 0,
  });
  // The shopkeeper's reason when the balance is not cleared — required, so a
  // shop that paid nothing can never be confused with a driver who tapped
  // through without asking.
  const [payNote, setPayNote] = useState('');

  /**
   * The UPI confirmation, and the van a spot sale came off.
   *
   * Both are only asked for when they apply: the screenshot when the money
   * came by UPI, the vehicle when a salesperson is delivering off somebody's
   * van rather than carrying goods from the godown.
   */
  const [upiShotId, setUpiShotId] = useState<string | null>(null);
  const [upiBusy, setUpiBusy] = useState(false);
  const [vehicleId, setVehicleId] = useState('');

  // Only the direct path can name a van; a route POD takes its vehicle from the
  // dispatch, and letting the request override that would let a driver
  // attribute his shortfall to somebody else's vehicle.
  const vehicles = useQuery<{
    items: { id: string; registration_number: string; taken_by: string | null;
             driver_name: string | null }[];
  }>(isDirect && !isDriver ? '/api/admin/masters/vehicles-today' : null);

  const acceptUpiShot = async (photo: CompressedImage) => {
    setUpiBusy(true);
    try {
      const up = await postFile<{ mediaId: string }>(
        '/api/media/upload', photo.blob, 'upi.jpg', { purpose: 'upi_screenshot' },
      );
      setUpiShotId(up.mediaId);
      toast.show('Screenshot attached', 'ok');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : 'Could not attach it', 'danger');
    } finally {
      setUpiBusy(false);
    }
  };
  const dispatchInfo = useQuery<{ items: { dispatch_id: string; invoice_number: string }[] }>(null);
  const invoice = useQuery<InvoiceDetail & { items: (InvoiceDetail['items'][number] & { id?: string })[] }>(
    invoiceId ? `/api/invoices/${invoiceId}` : null,
  );

  // The customer's whole ledger — every open invoice — so the delivery
  // executive can collect against any of them at the door, not only this bill.
  // Same source and shape the Collect Cash/UPI screen uses.
  const customerId = invoice.data?.invoice.customer_id;
  const ledger = useQuery<Ledger>(
    customerId ? `/api/invoices/ledger/${customerId}` : null,
    { deps: [customerId] },
  );

  // Resolve the invoice from the order list, keyed by the invoice number passed
  // through navigation state.
  const orders = useQuery<{ items: { id: string; invoice_id: string | null; invoice_number: string | null }[] }>(
    // Not fetched at all in direct mode: the invoice id is already known, and
    // pulling a hundred orders to look up something we were handed is a slow
    // request on a phone at a shop door.
    !directInvoiceId && state.invoiceNumber ? '/api/orders?pageSize=100' : null,
  );

  // Direct mode is handed the invoice id in the URL, so the lookup below —
  // which resolves an invoice from a dispatch's invoice NUMBER — is skipped.
  useEffect(() => {
    if (directInvoiceId) { setInvoiceId(directInvoiceId); return; }
    if (!state.invoiceNumber || !orders.data) return;
    const match = orders.data.items.find((o) => o.invoice_number === state.invoiceNumber);
    if (match?.invoice_id) setInvoiceId(match.invoice_id);
  }, [orders.data, state.invoiceNumber]);

  const [photo, setPhoto] = useState<CompressedImage | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  // Which camera sheet is open, if any. One state, because two sheets open at
  // once would fight over the same camera device.
  const [camera, setCamera] = useState<null | 'pod' | 'upi'>(null);
  // After a successful delivery, offer to set a follow-up before leaving (item #5).
  const [showReminder, setShowReminder] = useState(false);
  const [delivered, setDelivered] = useState<Record<number, number>>({});
  const [shortage, setShortage] = useState<Record<number, number>>({});
  const [shortageReason, setShortageReason] = useState<Record<number, string>>({});
  // Which open invoices the collection is being allocated against. Every open
  // invoice is ticked when the ledger loads — including the one being delivered.
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [failNote, setFailNote] = useState('');

  // Default to full delivery — the common case should be one tap.
  useEffect(() => {
    if (!invoice.data) return;
    setDelivered(Object.fromEntries(invoice.data.items.map((i) => [i.line_no, i.quantity])));
  }, [invoice.data]);

  useEffect(() => () => { if (preview) revokePreview(preview); }, [preview]);

  /**
   * The delivery photo, from a REAL camera.
   *
   * `<input capture>` is a hint that an installed PWA and an Android WebView
   * both ignore, so the gallery kept opening however the attribute was set —
   * which let a delivery be evidenced with a picture from last week. The
   * camera sheet is not a hint.
   */
  /**
   * The GPS fix, captured in the BACKGROUND the moment the photo is taken, so
   * it is ready by the time the driver taps "Mark delivered". Blocking the
   * submit on a fresh fix made the button spin for up to six seconds at the
   * shop door — the single biggest delay in the flow. The driver has just
   * photographed the delivery, which is exactly when he is standing at the
   * right place, so this is also the right moment to read the location.
   */
  const coordsPromise = useRef<Promise<
    { latitude: number; longitude: number; accuracyM: number } | null
  > | null>(null);

  function acceptPhoto(captured: CompressedImage) {
    if (preview) revokePreview(preview);
    setPhoto(captured);
    setPreview(previewUrl(captured.blob));
    // Fire-and-store. A rejection resolves to null — a POD without a pin is
    // still a POD, the photo is the mandatory evidence.
    coordsPromise.current = getCoords({ timeoutMs: 6000 }).catch(() => null);
  }

  const totalDelivered = Object.values(delivered).reduce((s, n) => s + n, 0);
  const totalShortage = Object.values(shortage).reduce((s, n) => s + n, 0);
  const hasUnexplainedShortage = Object.entries(shortage)
    .some(([line, qty]) => qty > 0 && !shortageReason[Number(line)]?.trim());

  /**
   * What the shop owes on THIS invoice right now.
   *
   * `balance_amount_paise` rather than the net amount, because a customer may
   * already have paid something against it — an advance, or a collection on an
   * earlier visit. Offering "Full payment" for the whole invoice when half is
   * already paid would take the money twice.
   */
  const balanceDue = invoice.data?.invoice.balance_amount_paise
    ?? invoice.data?.invoice.net_amount_paise
    ?? 0;

  // The customer's open invoices (this one included), for the tick-to-allocate
  // list. Cancelled and settled invoices are left out.
  const unpaid = useMemo(
    () => (ledger.data?.invoices ?? []).filter(
      (i) => i.status !== 'cancelled' && ['unpaid', 'part_paid', 'overdue'].includes(i.payment_status),
    ),
    [ledger.data],
  );

  // Tick every open invoice once the ledger loads — the shop is more likely to
  // clear its whole balance at the door than to pay this one bill alone.
  useEffect(() => {
    if (!ledger.data) return;
    const next: Record<string, boolean> = {};
    unpaid.forEach((i) => { next[i.id] = true; });
    setChecked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger.data]);

  // Due = the balances of only the ticked invoices, which is what the card
  // collects against (mirrors "Due on selected invoices" on Collect).
  const due = unpaid.filter((i) => checked[i.id]).reduce((s, i) => s + i.balance_amount_paise, 0);

  // The header figure is the rest of the ledger — everything the shop owes
  // APART from the bill being delivered right now.
  const accountOutstanding = ledger.data?.outstandingPaise ?? invoice.data?.customerOutstandingPaise ?? 0;
  const outstandingExclCurrent = Math.max(0, accountOutstanding - balanceDue);

  /**
   * The card's figures, from one place. `collectedPaise` is cash + UPI; the
   * outcome is derived from it against the selected total rather than chosen, so
   * the word and the money can never disagree. An over-tender leaves a non-zero
   * `advancePaise` and is kept on account (see the receipts below).
   */
  const pay = derivePayment(due, payment);
  const collectedPaise = pay.receivedPaise;
  const payOutcome: 'full' | 'part' | 'none' =
    collectedPaise <= 0 ? 'none' : pay.balancePaise <= 0 ? 'full' : 'part';
  const outAfter = Math.max(0, accountOutstanding - collectedPaise);

  /** Any UPI at all — a split that includes UPI still needs the screenshot. */
  const anyUpi = payment.upiPaise > 0;

  async function submit() {
    if (!photo) { toast.show(t('pod.photoRequired'), 'danger'); return; }
    if (!invoice.data) return;
    if (hasUnexplainedShortage) { toast.show(t('pod.shortageReason'), 'danger'); return; }
    // The "Why" note is optional here, matching the Collect flow — a shop may
    // simply be paying part of its ledger, which is not a fault to explain.
    // A UPI collection needs its screenshot — the server refuses it without
    // one. Caught here so the driver is not bounced after the fact.
    if (anyUpi && collectedPaise > 0 && !upiShotId) {
      toast.show('Photograph the UPI confirmation.', 'danger');
      return;
    }
    // Over-tender is allowed now: the surplus is kept as an advance on the
    // customer's account (see the capped allocations below).

    setBusy(true);
    try {
      // Use the fix started in the background when the photo was taken. By now
      // it has almost always resolved, so this adds no wait. If the photo path
      // somehow skipped it, fall back to a SHORT fix rather than the old 6s —
      // a delivery must not hang on a pin that may never come.
      const coords = await (coordsPromise.current
        ?? getCoords({ timeoutMs: 2500 }).catch(() => null));

      const lines = invoice.data.items.map((item) => ({
        invoiceItemId: (item as { id?: string }).id ?? '',
        deliveredQuantity: delivered[item.line_no] ?? 0,
        shortageQuantity: shortage[item.line_no] ?? 0,
        shortageReason: shortageReason[item.line_no]?.trim() || undefined,
      }));

      const outcome = totalShortage > 0 ? 'partially_delivered' : 'delivered';

      /**
       * THE MONEY — ONE ATOMIC DOOR COLLECTION, SERVER-ALLOCATED.
       *
       * Built once here so the online post and the offline enqueue carry the
       * same `clientUuid`: a retry of either must land the collection once, not
       * twice. Each non-zero tender is a row (`payments.payment_mode` is what
       * the day-close reconciliation reads); the server allocates oldest-first
       * across the ticked invoices from the balances IT reads, so a queued
       * collection that drains later lands against what is actually owed then —
       * not against stale numbers this screen froze at submit (the bug that
       * chased a shop for ₹2,463 it had already paid).
       */
      const tenders = ([
        { paymentMode: 'cash' as const, amountPaise: payment.cashPaise },
        { paymentMode: 'upi' as const, amountPaise: payment.upiPaise },
      ]).filter((tnd) => tnd.amountPaise > 0);
      const doorBody = tenders.length > 0 ? {
        customerId: invoice.data.invoice.customer_id,
        tenders,
        // The invoices the shop ticked; the server caps each at its balance.
        invoiceIds: unpaid.filter((inv) => checked[inv.id]).map((inv) => inv.id),
        // One UPI tender at the door; its screenshot rides at the top level so
        // the outbox can substitute a stashed one on drain.
        ...(anyUpi && upiShotId ? { proofMediaId: upiShotId } : {}),
        remarks: payNote.trim() || undefined,
        clientUuid: crypto.randomUUID(),
      } : null;

      /**
       * The amount, decided from the outcome rather than typed twice.
       *
       * "Full" means the whole balance — asking a driver to key the figure he
       * can already see is how a digit goes missing. "Part" is the only case
       * where a number is entered, and "none" is zero by definition.
       */
      const body = {
        photoMediaId: '', // replaced below or substituted by the outbox
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        lines,
        outcome,
        driverRemarks: remarks.trim() || undefined,
        // A van only on the direct path, and only when one was chosen.
        ...(isDirect && vehicleId ? { vehicleId } : {}),
        payment: {
          outcome: payOutcome,
          amountPaise: collectedPaise,
          /**
           * Always the two amounts, which sum to amountPaise. The server reads
           * a null mode with a non-zero amount as a split (migration 0040), and
           * the card is a split model by nature — all cash is just upi = 0.
           */
          ...(payOutcome === 'none'
            ? {}
            : { cashPaise: payment.cashPaise, upiPaise: payment.upiPaise }),
          note: payNote.trim() || undefined,
          // The server refuses any collection containing UPI without it.
          ...(payOutcome !== 'none' && anyUpi && upiShotId
            ? { upiScreenshotMediaId: upiShotId }
            : {}),
        },
      };

      if (!online) {
        // The photo is stashed locally and uploaded first when the queue
        // drains; the outbox substitutes the real media id into the payload.
        const localMediaId = await outbox.stashMedia(photo.blob, 'pod_photo');
        await outbox.enqueue({
          method: 'POST',
          path: podPath,
          body,
          label: `${t('pod.title')} — ${state.shopName ?? ''}`,
          kind: 'pod',
          mediaIds: [localMediaId],
        });
        /**
         * OFFLINE, THE MONEY IS ENQUEUED TOO — it used to be dropped.
         *
         * The old screen posted payments only on the online path, so a door
         * collection taken out of signal recorded the delivery and silently
         * recorded no ledger payment at all. A UPI tender can't be captured
         * offline (its screenshot uploads on the spot, and submit is blocked
         * above without it), so an offline collection is cash — no proof to
         * stash. The server allocates it oldest-first when the queue drains.
         * Enqueued AFTER the POD so it drains after it, keeping the order the
         * outbox depends on; the shared clientUuid makes the retry idempotent.
         */
        if (doorBody) {
          await outbox.enqueue({
            method: 'POST',
            path: '/api/payments/door',
            body: doorBody,
            label: `${t('pay.atDoor')} — ${state.shopName ?? ''}`,
            kind: 'payment',
          });
        }
        toast.show(t('app.offlineHint'), 'ok');
        navigate(afterPodPath);
        return;
      }

      const uploaded = await uploadMedia({
        blob: photo.blob,
        purpose: 'pod_photo',
        originalSizeBytes: photo.originalSizeBytes,
        widthPx: photo.width,
        heightPx: photo.height,
      });

      const result = await api.post<{
        deliveredQuantity: number; shortageQuantity: number; autoConfirmHours: number;
      }>(podPath, { ...body, photoMediaId: uploaded.mediaId });

      /**
       * THE MONEY GOES THROUGH /api/payments/door, NOT THROUGH THE POD.
       *
       * One atomic request for the whole collection: the server writes one row
       * per tender and allocates oldest-first across the ticked invoices from
       * the balances it reads. It does the allocation this screen used to do by
       * hand — which removed two sharp edges. The two-call loop could land one
       * tender and lose the other (a half-recorded collection); and the client
       * allocation it sent, frozen at submit, could exceed a now-smaller balance
       * by the time an offline retry drained, which the server 400s and the
       * outbox then abandons. One request, server-side allocation, fixes both.
       *
       * Queued rather than posted inline on failure: the POD has already
       * succeeded and the goods are delivered. Losing the payment because a
       * shop has no signal would be the worst outcome available, and the outbox
       * (with the stable clientUuid) is what the rest of this screen relies on.
       */
      if (doorBody) {
        try {
          await api.post('/api/payments/door', doorBody);
        } catch {
          await outbox.enqueue({
            method: 'POST',
            path: '/api/payments/door',
            body: doorBody,
            label: `${t('pay.atDoor')} — ${state.shopName ?? ''}`,
            kind: 'payment',
          });
          // 'danger', not 'warn': ToastTone has three values and 'warn' is not one of
          // them. A payment that has not reached the server is not a neutral note.
          toast.show(t('pay.queuedOffline'), 'danger');
        }
      }

      toast.show(
        `${result.deliveredQuantity} delivered · customer confirms within ${result.autoConfirmHours}h`,
        'ok',
      );
      // Offer a follow-up reminder before leaving the stop; navigating happens
      // when that sheet is closed (set or skipped). Item #5.
      setShowReminder(true);
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function reportFailure() {
    if (!failReason) return;
    setBusy(true);
    try {
      let photoMediaId: string | undefined;
      if (photo && online) {
        const uploaded = await uploadMedia({ blob: photo.blob, purpose: 'pod_photo' });
        photoMediaId = uploaded.mediaId;
      }
      const result = await api.post<{ message: string; options: string[] }>(
        `/api/deliveries/${dispatchId}/failed`,
        { failureReason: failReason, failureNote: failNote.trim() || undefined, photoMediaId },
      );
      toast.show(result.message, 'neutral');
      navigate(afterPodPath);
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
      setFailOpen(false);
    }
  }

  if (invoice.loading || orders.loading) {
    return <Page title={t('pod.title')} back><Skeleton rows={4} /></Page>;
  }
  if (!invoice.data) {
    return (
      <Page title={t('pod.title')} back>
        <Empty title="Could not load the invoice" hint="Go back and try the stop again" />
      </Page>
    );
  }

  return (
    <Page
      title={state.shopName ?? t('pod.title')}
      subtitle={invoice.data.invoice.invoice_number}
      back={`/routes/${state.loadSheetId ?? ''}`}
    >
      <div className="stack">
        {/*
          Customer card, identical in style to the Collect Cash/UPI screen: the
          customer name and code on the left, and their FULL outstanding across
          every open invoice in red on the right — so the driver can ask for the
          whole pending amount at the door, not only this bill. The outstanding
          is shown ONLY when there is one; a shop with a clear ledger shows just
          its name, and the current bill is handled by the Payment section below.
        */}
        <Card>
          <div className="row-between">
            <div style={{ minWidth: 0 }}>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className="t-semi t-clip" style={classificationNameStyle(invoice.data.invoice.customer_classification)}>
                  {invoice.data.invoice.customer_name
                    ?? state.customerName
                    ?? invoice.data.invoice.shop_name}
                </span>
                {invoice.data.invoice.customer_classification
                  && invoice.data.invoice.customer_classification !== 'trusted' && (
                  <ClassificationBadge classification={invoice.data.invoice.customer_classification} />
                )}
              </div>
              <div className="t-xs t-muted">
                {invoice.data.invoice.customer_code ?? invoice.data.invoice.shop_name}
              </div>
            </div>
            {outstandingExclCurrent > 0 && (
              <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                <div className="t-xs t-muted">{t('ledger.outstanding')}</div>
                <Money
                  paise={outstandingExclCurrent}
                  className="t-lg"
                  tone="danger"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Mandatory, and marked as such. The server rejects a POD without a
            real photo, so blocking here saves a wasted round trip in a shop. */}
        <Card>
          <PhotoTile
            label={t('pod.title')}
            hint={t('pod.photoHint')}
            required
            previewUrl={preview}
            onCapture={() => setCamera('pod')}
            onRemove={() => { if (preview) revokePreview(preview); setPhoto(null); setPreview(null); }}
          />
          {!photo && <Banner tone="warn"><span className="t-xs">{t('pod.photoRequired')}</span></Banner>}
        </Card>

        <Card flush>
          <div className="card__header"><h3>{t('pod.deliveredQty')}</h3></div>
          <div className="list">
            {(() => {
              /*
                SHOW IT THE WAY IT WAS PLACED — one row per PRODUCT.

                The cart lets a product be ordered as a box line AND a loose-pieces
                line, shown together at checkout as "4 Pets + 2 Pieces". Grouping a
                product's lines here presents the delivery the same way: one header
                with the invoiced total in pets + pieces (off base_quantity, so it
                reconciles with van stock), and the shortage entered in EACH line's
                own unit — Pets for a box line, Pieces for a loose line. That keeps
                the entry lossless and needs no change to how the server bills it:
                every line is still sent in its own sold unit.

                (A box-only line is counted in whole Pets; a part-box short is not
                representable, which is correct — you cannot bill a fraction of a
                box the shop did not order loose.)
              */
              const groups = new Map<string, typeof invoice.data.items>();
              for (const it of invoice.data.items) {
                const g = groups.get(it.sku_code);
                if (g) g.push(it); else groups.set(it.sku_code, [it]);
              }
              return [...groups.values()].map((lines) => {
                const head = lines[0]!;
                const pack = { baseUom: head.bc_base_uom ?? head.uom, packUom: head.bc_pack_uom, packQty: head.pack_qty };
                const invoicedBase = lines.reduce((s, l) => s + (l.base_quantity ?? l.quantity), 0);
                const freeTotal = lines.reduce((s, l) => s + l.free_quantity, 0);
                return (
                  <div key={head.sku_code} className="list__item list__item--static" style={{ flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 100%', minWidth: 0 }}>
                      <div className="t-sm t-semi">{head.product_name}</div>
                      <div className="t-xs t-muted">
                        {head.sku_code} · invoiced {piecesAndPacks(invoicedBase, pack)}
                        {freeTotal > 0 && ` +${freeTotal} free`}
                      </div>
                    </div>
                    {lines.map((item) => {
                      const short = shortage[item.line_no] ?? 0;
                      // The line's own unit — a box line is counted in Pets, a
                      // loose line in Pieces. This is what makes the entry pets
                      // and pieces, while each line stays in its sold unit.
                      const unit = item.sold_uom === 'BOX' ? 'Pets' : 'Pieces';
                      const u = unit.toLowerCase();
                      return (
                        <div key={item.line_no} style={{ width: '100%', marginTop: 8 }}>
                          <div className="row-between" style={{ gap: 8, alignItems: 'center' }}>
                            <span className="t-xs t-muted">
                              {unit} {t('pod.notDeliveredQty').toLowerCase()} · of {item.quantity} {u}
                            </span>
                            <Stepper
                              value={short}
                              onChange={(n) => {
                                const notDelivered = Math.min(n, item.quantity);
                                setShortage((prev) => ({ ...prev, [item.line_no]: notDelivered }));
                                setDelivered((prev) => ({
                                  ...prev, [item.line_no]: Math.max(0, item.quantity - notDelivered),
                                }));
                              }}
                              min={0}
                              max={item.quantity}
                            />
                          </div>
                          <div className="t-xs t-muted" style={{ textAlign: 'right' }}>
                            {t('pod.deliveredQty')} {Math.max(0, item.quantity - short)} {u}
                          </div>
                          {short > 0 && (
                            <div style={{ width: '100%', marginTop: 6 }}>
                              <TextInput
                                label={`${t('pod.shortageReason')} — ${short} ${u} short`}
                                required
                                value={shortageReason[item.line_no] ?? ''}
                                error={!shortageReason[item.line_no]?.trim() ? t('app.required') : undefined}
                                onChange={(e) => setShortageReason((prev) => ({ ...prev, [item.line_no]: e.target.value }))}
                                maxLength={300}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </Card>

        {/*
          PAYMENT, AT THE DOOR — the same tick-to-allocate flow as Collect
          Cash/UPI, so the shopkeeper can clear any of their open bills, not only
          this delivery's. Every open invoice is listed and ticked by default;
          the card collects against the ticked total, cash taking the oldest
          invoices first and UPI what is left, with any over-tender kept as an
          advance on account.
        */}
        {unpaid.length > 0 && (
          <Card flush>
            <div className="card__header"><h3>Pending invoices · tick to allocate</h3></div>
            <div className="list">
              {unpaid.map((i) => {
                const overdue = i.due_date ? daysBetween(i.due_date) : 0;
                return (
                  <label key={i.id} className="list__item" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!checked[i.id]}
                      onChange={(e) => setChecked((prev) => ({ ...prev, [i.id]: e.target.checked }))}
                      style={{ width: 18, height: 18, accentColor: 'var(--brand)', marginRight: 10, flex: '0 0 auto' }}
                    />
                    <span className="grow" style={{ minWidth: 0 }}>
                      <span className="row" style={{ gap: 8 }}>
                        <span className="t-sm t-semi">{i.invoice_number}</span>
                        {overdue > 0 && <Chip tone="danger">{overdue}d</Chip>}
                      </span>
                      <span className="t-xs t-muted">{dateShort(i.invoice_date)}</span>
                    </span>
                    <Money paise={i.balance_amount_paise} className="t-sm" />
                  </label>
                );
              })}
            </div>
          </Card>
        )}

        <PaymentCard
          totalPaise={due}
          value={payment}
          onChange={setPayment}
          showDiscount={false}
          totalLabel="Due on selected invoices"
        />

        <Card className="stack-2">
          <div className="row-between">
            <span className="t-sm t-semi">Outstanding after this collection</span>
            <Money paise={outAfter} className="t-sm" tone={outAfter > 0 ? 'danger' : undefined} />
          </div>

          {/*
            THE UPI SCREENSHOT.

            UPI has no check at the door — the money goes to an account nobody
            there can see, so a declared UPI payment that never arrived looks
            identical to one that did. The server refuses a UPI collection
            without it, so this is not optional dressing.
          */}
          {anyUpi && (
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <Button
                size="sm"
                variant={upiShotId ? 'quiet' : 'primary'}
                busy={upiBusy}
                onClick={() => setCamera('upi')}
              >
                {upiShotId ? 'Retake screenshot' : 'Photograph the UPI confirmation'}
              </Button>
              {upiShotId && <Chip tone="ok">Attached</Chip>}
            </div>
          )}

          <TextArea
            label="Why"
            value={payNote}
            onChange={(e) => setPayNote(e.target.value)}
            placeholder="What the customer said (optional)"
            maxLength={500}
          />

          {payment.cashPaise > 0 && (
            <span className="t-xs t-muted">
              {t('pay.intoCashInHand')}
            </span>
          )}
        </Card>

        {/*
          WHICH VAN THE GOODS CAME OFF — a salesperson's spot sale.

          Only on the direct path. Naming the van is what makes the sale appear
          in that driver's app; without it he reconciles at close against stock
          that left without his knowledge, and the variance is his to explain.

          Blank is valid and means the godown: goods the salesperson carried
          himself, whose stock Business Central reduces when it posts.
        */}
        {isDirect && !isDriver && (vehicles.data?.items.length ?? 0) > 0 && (
          <Card>
            <Select
              label="Delivered from"
              value={vehicleId}
              placeholder="Godown — I carried it myself"
              options={(vehicles.data?.items ?? []).map((v) => ({
                value: v.id,
                label: v.driver_name
                  ? `${v.registration_number} — ${v.driver_name}`
                  : v.registration_number,
              }))}
              onChange={(e) => setVehicleId(e.target.value)}
              hint="If it came off a van, say which — the driver sees it in his app."
            />
          </Card>
        )}

        <TextArea
          label={t('pod.remarks')}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={500}
        />

        <div className="row-between">
          <Chip tone={totalShortage > 0 ? 'warn' : 'ok'}>
            {totalDelivered} {t('pod.deliveredQty').toLowerCase()}
          </Chip>
          {totalShortage > 0 && <Chip tone="danger">{totalShortage} {t('pod.shortageQty').toLowerCase()}</Chip>}
        </div>

        <Button variant="quiet" block onClick={() => setFailOpen(true)}>
          {t('pod.markFailed')}
        </Button>
      </div>

      <div className="actionbar">
        <Button
          block size="lg" busy={busy}
          disabled={!photo || hasUnexplainedShortage}
          onClick={() => void submit()}
        >{t('pod.markDelivered')}</Button>
      </div>

      <Sheet
        open={failOpen}
        onClose={() => setFailOpen(false)}
        title={t('pod.markFailed')}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setFailOpen(false)} disabled={busy}>
              {t('app.cancel')}
            </Button>
            <Button variant="danger" block busy={busy} disabled={!failReason} onClick={() => void reportFailure()}>
              {t('app.confirm')}
            </Button>
          </>
        }
      >
        <div className="stack-4">
          <Select
            label={t('pod.failureReason')}
            required
            value={failReason}
            placeholder="Select"
            options={FAILURE_REASONS.map((r) => ({ value: r, label: t(`pod.${r}`) }))}
            onChange={(e) => setFailReason(e.target.value)}
          />
          <TextArea
            label={t('visit.notes')}
            value={failNote}
            onChange={(e) => setFailNote(e.target.value)}
            maxLength={500}
          />
          {/* Admin decides between reschedule, return to warehouse and
              reassign — the driver does not choose. Saying so avoids him
              waiting at the shop for an answer he was not going to get. */}
          <Banner tone="info">
            <span className="t-sm">{t('pod.awaitingAdmin')}</span>
          </Banner>
        </div>
      </Sheet>

      {/*
        ONE SHEET, TWO PURPOSES.

        The delivery photo faces the goods; the UPI screenshot faces whatever
        the shopkeeper is holding, so both use the rear camera. They share one
        piece of state because two sheets open at once would fight over the
        same camera device and the second would fail to start.
      */}
      {/* The UPI confirmation is usually a screenshot, so it gets the three-way
          chooser (item #10). The POD evidence photo stays live-camera only. */}
      {camera === 'upi' ? (
        <ImageCaptureSheet
          open
          facing="environment"
          title="Attach the UPI confirmation"
          compressOptions={{ maxDimension: 1600 }}
          onCapture={(img) => void acceptUpiShot(img)}
          onClose={() => setCamera(null)}
        />
      ) : (
        <CameraSheet
          open={camera === 'pod'}
          facing="environment"
          title={t('pod.photoHint')}
          compressOptions={{ maxDimension: 1600, maxBytes: 2_500_000 }}
          onCapture={acceptPhoto}
          onClose={() => setCamera(null)}
        />
      )}

      {/* After a delivery: offer a follow-up (item #5). Closing it — set or
          skipped — leaves the stop. */}
      {showReminder && customerId && (
        <ReminderSheet
          open
          customerId={customerId}
          customerName={invoice.data?.invoice.customer_name}
          source="delivery"
          onClose={() => navigate(afterPodPath)}
          onDone={() => navigate(afterPodPath)}
        />
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Load-in — the control point
// ---------------------------------------------------------------------------

/**
 * Count residual stock back into the warehouse.
 *
 * The variance is computed as:
 *
 *   expected = loaded − delivered − extra-box sold + returns picked up
 *
 * A mismatch WITHOUT a reason is flagged against the driver and raised to
 * Admin. The screen warns before submit, because a driver who understands that
 * is far more likely to write a real reason than a shrug.
 */
export function LoadIn() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const vanStock = useQuery<{
    loadSheetNumber: string;
    status: string;
    items: {
      productId: string; skuCode: string; productName: string;
      loaded: number; delivered: number; extraBoxSold: number; returnedIn: number;
      expectedResidual: number;
      packUom: string | null; packQty: number | null;
    }[];
  }>(id ? `/api/stock/load-sheets/${id}/van-stock` : null);

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [odometer, setOdometer] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!vanStock.data) return;
    // Pre-fill with the expected figure. The driver adjusts only what differs,
    // which keeps a clean route to a couple of taps.
    setCounts(Object.fromEntries(vanStock.data.items.map((i) => [i.productId, i.expectedResidual])));
  }, [vanStock.data]);

  const variances = (vanStock.data?.items ?? []).map((item) => {
    const actual = counts[item.productId] ?? item.expectedResidual;
    return { ...item, actual, variance: actual - item.expectedResidual };
  });

  const withVariance = variances.filter((v) => v.variance !== 0);
  const unexplained = withVariance.filter((v) => !reasons[v.productId]?.trim());

  async function submit() {
    if (!vanStock.data) return;
    setBusy(true);
    try {
      const result = await api.post<{
        varianceFlagged: boolean; hasVariance: boolean; message: string;
      }>(`/api/stock/load-sheets/${id}/load-in`, {
        closingOdometer: odometer ? Number(odometer) : undefined,
        counts: vanStock.data.items.map((item) => ({
          productId: item.productId,
          actualResidualQuantity: counts[item.productId] ?? item.expectedResidual,
          varianceReason: reasons[item.productId]?.trim() || undefined,
        })),
      });
      toast.show(result.message, result.varianceFlagged ? 'danger' : 'ok');
      navigate('/routes');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  if (vanStock.loading && !vanStock.data) {
    return <Page title={t('route.loadIn')} back><Skeleton rows={4} /></Page>;
  }
  if (!vanStock.data) return <Page title={t('route.loadIn')} back><Empty /></Page>;

  return (
    <Page title={t('route.loadIn')} subtitle={vanStock.data.loadSheetNumber} back={`/routes/${id}`}>
      <div className="stack">
        <Banner tone="info" title={t('route.loadInHint')}>
          <span className="t-sm">{t('route.varianceRequired')}</span>
        </Banner>

        <Card flush>
          <div className="list">
            {variances.map((item) => (
              <div key={item.productId} className="list__item list__item--static" style={{ flexWrap: 'wrap' }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{item.productName}</div>
                  <div className="t-xs t-muted">
                    {t('route.loaded')} {item.loaded} · {t('route.delivered')} {item.delivered}
                    {item.extraBoxSold > 0 && ` · ${t('route.extraSold')} ${item.extraBoxSold}`}
                    {item.returnedIn > 0 && ` · returned ${item.returnedIn}`}
                  </div>
                  <div className="t-xs t-semi" style={{ marginTop: 2 }}>
                    {t('route.expected')}: {item.expectedResidual}
                    {/*
                      THE SAME FIGURE IN CARTONS.

                      A driver counts boxes off the van floor. "288" is a number
                      he can only check by opening cartons; "12 box" he can
                      check by looking. Requested 2 Oct as box and piece
                      quantities being visible in the driver app.
                    */}
                    {item.packQty && item.packQty > 1 && (
                      <span className="t-muted">
                        {' '}({piecesAndPacks(item.expectedResidual, {
                          baseUom: 'PCS', packUom: item.packUom, packQty: item.packQty,
                        })})
                      </span>
                    )}
                  </div>
                </div>

                <div className="t-right" style={{ flex: '0 0 auto' }}>
                  <Stepper
                    value={item.actual}
                    onChange={(n) => setCounts((prev) => ({ ...prev, [item.productId]: n }))}
                    min={0}
                    max={item.loaded}
                  />
                  {item.variance !== 0 && (
                    <div className={`t-xs t-semi ${item.variance < 0 ? 't-danger' : 't-warn'}`} style={{ marginTop: 4 }}>
                      {t('route.variance')} {item.variance > 0 ? '+' : ''}{item.variance}
                    </div>
                  )}
                </div>

                {item.variance !== 0 && (
                  <div style={{ width: '100%', marginTop: 8 }}>
                    <TextInput
                      label={t('route.varianceReason')}
                      required
                      value={reasons[item.productId] ?? ''}
                      error={!reasons[item.productId]?.trim() ? t('app.required') : undefined}
                      onChange={(e) => setReasons((prev) => ({ ...prev, [item.productId]: e.target.value }))}
                      maxLength={500}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        <TextInput
          label={t('route.odometer')}
          numeric
          value={odometer}
          onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))}
        />

        {unexplained.length > 0 && (
          <Banner tone="danger" title="Reason needed">
            <span className="t-sm">
              {unexplained.length} SKU(s) differ with no reason. This will be flagged
              for admin review.
            </span>
          </Banner>
        )}
      </div>

      <div className="actionbar">
        <Button block size="lg" busy={busy} onClick={() => setConfirmOpen(true)}>
          {t('route.closeRoute')}
        </Button>
      </div>

      <Sheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('route.closeRoute')}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setConfirmOpen(false)} disabled={busy}>
              {t('app.cancel')}
            </Button>
            <Button
              variant={unexplained.length > 0 ? 'warn' : 'primary'}
              block busy={busy} onClick={() => void submit()}
            >{t('app.confirm')}</Button>
          </>
        }
      >
        <div className="stack-2">
          {withVariance.length === 0 ? (
            <Banner tone="ok">Everything matches. Nothing to explain.</Banner>
          ) : (
            <>
              <p className="t-sm t-muted">{withVariance.length} SKU(s) differ from expected:</p>
              {withVariance.map((v) => (
                <div key={v.productId} className="row-between t-sm">
                  <span className="t-clip">{v.productName}</span>
                  <span className={v.variance < 0 ? 't-danger t-semi' : 't-warn t-semi'}>
                    {v.variance > 0 ? '+' : ''}{v.variance}
                  </span>
                </div>
              ))}
              {unexplained.length > 0 && (
                <Banner tone="danger">
                  <span className="t-sm">
                    {unexplained.length} without a reason will be flagged against you.
                  </span>
                </Banner>
              )}
            </>
          )}
        </div>
      </Sheet>
    </Page>
  );
}
